// src/pages/Game.js - WITH CSS VARIABLES & NO DUPLICATE HEADER
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChefHat, Plus, X, Sparkles, Trash2, Check, Utensils, Clock,
  Globe, Star, Trophy, Zap, Shuffle
} from 'lucide-react';

import { recipeDatabase, cuisines } from '../data/recipeData';

const achievementsList = [
  { id: 'first_cook', name: 'First Cook!', description: 'Cook your first recipe', icon: '🍳', requirement: 1 },
  { id: 'chef_apprentice', name: 'Chef Apprentice', description: 'Cook 5 recipes', icon: '👨‍🍳', requirement: 5 },
  { id: 'master_chef', name: 'Master Chef', description: 'Cook 10 recipes', icon: '👑', requirement: 10 },
  { id: 'ingredient_collector', name: 'Ingredient Collector', description: 'Add 10 ingredients', icon: '🥘', requirement: 10 },
  { id: 'sauce_master', name: 'Sauce Master', description: 'Add 5 sauces', icon: '🧂', requirement: 5 },
  { id: 'world_traveler', name: 'World Traveler', description: 'Cook from 5 different cuisines', icon: '🌍', requirement: 5 },
];

const Game = () => {
  const [ingredients, setIngredients] = useState([]);
  const [sauces, setSauces] = useState([]);
  const [currentIngredient, setCurrentIngredient] = useState('');
  const [currentSauce, setCurrentSauce] = useState('');
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [selectedSauces, setSelectedSauces] = useState([]);
  const [selectedCuisine, setSelectedCuisine] = useState('All');
  const [recipes, setRecipes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showPuzzle, setShowPuzzle] = useState(false);
  const [xp, setXp] = useState(0);
  const [level, setLevel] = useState(1);
  const [recipesCooked, setRecipesCooked] = useState([]);
  const [highlightRecipe, setHighlightRecipe] = useState(null);
  const [achievements, setAchievements] = useState([]);
  
  const [showIngredientSuggestions, setShowIngredientSuggestions] = useState(false);
  const [showSauceSuggestions, setShowSauceSuggestions] = useState(false);
  const [selectedRecipeForModal, setSelectedRecipeForModal] = useState(null);

  const allKnownIngredients = useMemo(() => {
    const ingredientSet = new Set();
    recipeDatabase.forEach(recipe => {
      recipe.ingredients.forEach(ing => ingredientSet.add(ing.toLowerCase()));
    });
    return Array.from(ingredientSet).sort();
  }, []);

  const allKnownSauces = useMemo(() => {
    const sauceSet = new Set();
    recipeDatabase.forEach(recipe => {
      recipe.sauces.forEach(sauce => sauceSet.add(sauce.toLowerCase()));
    });
    return Array.from(sauceSet).sort();
  }, []);

  const ingredientSuggestions = useMemo(() => {
    if (!currentIngredient.trim()) return [];
    const query = currentIngredient.toLowerCase().trim();
    return allKnownIngredients.filter(ing => ing.includes(query) && !ingredients.includes(ing)).slice(0, 5);
  }, [currentIngredient, ingredients, allKnownIngredients]);

  const sauceSuggestions = useMemo(() => {
    if (!currentSauce.trim()) return [];
    const query = currentSauce.toLowerCase().trim();
    return allKnownSauces.filter(sauce => sauce.includes(query) && !sauces.includes(sauce)).slice(0, 5);
  }, [currentSauce, sauces, allKnownSauces]);

  useEffect(() => { loadPantry(); }, []);

  useEffect(() => {
    setLevel(prev => {
      const next = Math.floor(xp / 100) + 1;
      if (next > prev) {
        console.log('🎵 LEVEL UP! 🎵');
        return next;
      }
      return prev;
    });
  }, [xp]);

  const loadPantry = async () => {
    setIsLoading(true);
    try {
      const ingredientsData = localStorage.getItem('pantry-ingredients');
      const saucesData = localStorage.getItem('pantry-sauces');
      const xpData = localStorage.getItem('game-points');
      const levelData = localStorage.getItem('game-level');
      const recipesData = localStorage.getItem('recipes-cooked');
      const achievementsData = localStorage.getItem('achievements');

      if (ingredientsData) setIngredients(JSON.parse(ingredientsData));
      if (saucesData) setSauces(JSON.parse(saucesData));
      if (xpData) setXp(parseInt(xpData));
      if (levelData) setLevel(parseInt(levelData));
      if (recipesData) setRecipesCooked(JSON.parse(recipesData));
      if (achievementsData) setAchievements(JSON.parse(achievementsData));
    } catch (error) {
      console.error('Error loading pantry:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const savePantry = (newIngredients, newSauces) => {
    try {
      localStorage.setItem('pantry-ingredients', JSON.stringify(newIngredients));
      localStorage.setItem('pantry-sauces', JSON.stringify(newSauces));
    } catch (error) {
      console.error('Error saving pantry:', error);
    }
  };

  const saveGameData = useCallback(() => {
    localStorage.setItem('game-points', xp.toString());
    localStorage.setItem('game-level', level.toString());
    localStorage.setItem('recipes-cooked', JSON.stringify(recipesCooked));
    localStorage.setItem('achievements', JSON.stringify(achievements));
    window.dispatchEvent(new Event('gameStatsUpdated'));
  }, [xp, level, recipesCooked, achievements]);

  const checkAchievements = useCallback(() => {
    const next = [...achievements];
    achievementsList.forEach(a => {
      if (!next.includes(a.id)) {
        let unlocked = false;
        if (a.id === 'first_cook' && recipesCooked.length >= 1) unlocked = true;
        if (a.id === 'chef_apprentice' && recipesCooked.length >= 5) unlocked = true;
        if (a.id === 'master_chef' && recipesCooked.length >= 10) unlocked = true;
        if (a.id === 'ingredient_collector' && ingredients.length >= 10) unlocked = true;
        if (a.id === 'sauce_master' && sauces.length >= 5) unlocked = true;
        if (a.id === 'world_traveler') {
          const unique = new Set(recipesCooked.map(r => r.cuisine));
          if (unique.size >= 5) unlocked = true;
        }
        if (unlocked) {
          next.push(a.id);
          console.log(`🏆 ACHIEVEMENT UNLOCKED: ${a.name}`);
        }
      }
    });
    if (next.length !== achievements.length) setAchievements(next);
  }, [achievements, ingredients, sauces, recipesCooked]);

  useEffect(() => {
    saveGameData();
    checkAchievements();
  }, [saveGameData, checkAchievements]);
  
  const addIngredient = (ing) => {
    const clean = ing.trim().toLowerCase();
    if (clean && !ingredients.includes(clean)) {
      const updated = [...ingredients, clean];
      setIngredients(updated);
      savePantry(updated, sauces);
      setCurrentIngredient('');
      setShowIngredientSuggestions(false);
    }
  };

  const removeIngredient = (ing) => {
    const updated = ingredients.filter(i => i !== ing);
    setIngredients(updated);
    savePantry(updated, sauces);
  };

  const addSauce = (sauce) => {
    const clean = sauce.trim().toLowerCase();
    if (clean && !sauces.includes(clean)) {
      const updated = [...sauces, clean];
      setSauces(updated);
      savePantry(ingredients, updated);
      setCurrentSauce('');
      setShowSauceSuggestions(false);
    }
  };

  const removeSauce = (sauce) => {
    const updated = sauces.filter(s => s !== sauce);
    setSauces(updated);
    savePantry(ingredients, updated);
  };

  const togglePuzzle = () => {
    setShowPuzzle(!showPuzzle);
    if (!showPuzzle) {
      setSelectedIngredients([]);
      setSelectedSauces([]);
    }
  };

  const handleSelectIngredient = (ing) => {
    setSelectedIngredients(prev =>
      prev.includes(ing) ? prev.filter(i => i !== ing) : [...prev, ing]
    );
  };

  const handleSelectSauce = (sauce) => {
    setSelectedSauces(prev =>
      prev.includes(sauce) ? prev.filter(s => s !== sauce) : [...prev, sauce]
    );
  };

  const findRecipes = () => {
    const targetIngs = showPuzzle ? selectedIngredients : ingredients;
    const targetSauces = showPuzzle ? selectedSauces : sauces;

    let matches = recipeDatabase.filter(recipe => {
      if (selectedCuisine !== 'All' && recipe.cuisine !== selectedCuisine) return false;
      return true;
    });

    matches = matches.map(r => {
      const ingMatches = r.ingredients.filter(ing => targetIngs.includes(ing.toLowerCase())).length;
      const sauceMatches = r.sauces.filter(s => targetSauces.includes(s.toLowerCase())).length;
      const totalRequired = r.ingredients.length + r.sauces.length;
      const totalMatched = ingMatches + sauceMatches;
      const matchPercentage = totalRequired > 0 ? (totalMatched / totalRequired) * 100 : 0;

      return {
        ...r,
        ingredientMatches: ingMatches,
        sauceMatches: sauceMatches,
        matchPercentage
      };
    });

    matches = matches.filter(r => r.matchPercentage > 0);
    matches.sort((a, b) => b.matchPercentage - a.matchPercentage);

    setRecipes(matches);
  };

  const surpriseMe = () => {
    const filtered = recipeDatabase.filter(r =>
      selectedCuisine === 'All' || r.cuisine === selectedCuisine
    );
    if (filtered.length === 0) return;
    const surprise = [filtered[Math.floor(Math.random() * filtered.length)]];
    setRecipes(surprise.map(r => ({ ...r, matchPercentage: 100, ingredientMatches: r.ingredients.length, sauceMatches: r.sauces.length })));
    setHighlightRecipe(surprise[0].name);
    setTimeout(() => setHighlightRecipe(null), 1500);
  };

  const cookRecipe = (recipe) => {
    setXp(prev => prev + recipe.points);
    if (!recipesCooked.some(r => r.name === recipe.name)) {
      setRecipesCooked(prev => [...prev, { name: recipe.name, cuisine: recipe.cuisine, cookedAt: new Date().toISOString() }]);
    }
    setHighlightRecipe(recipe.name);
    setTimeout(() => setHighlightRecipe(null), 1200);
  };

  const getCuisineFlag = (cuisine) => {
    const flags = {
      Indonesian: '🇮🇩',
      Japanese: '🇯🇵',
      Korean: '🇰🇷',
      Western: '🍔',
      Italian: '🇮🇹',
      Mexican: '🇲🇽',
      Chinese: '🇨🇳',
      Thai: '🇹🇭',
      Indian: '🇮🇳',
      Fusion: '✨',
      '✨ Fusion': '✨'
    };
    return flags[cuisine] || '🍽️';
  };

  const getRecipeDescription = (recipe) => {
    const sentences = recipe.instructions.split('. ');
    if (sentences.length >= 2) {
      return sentences.slice(0, 2).join('. ') + '.';
    }
    return sentences[0] + (sentences[0].endsWith('.') ? '' : '.');
  };

  const formatInstructions = (instructions) => {
    // Split by '. ' and filter out empty strings
    const steps = instructions.split('. ').filter(step => step.trim().length > 0);
    return steps.map((step, index) => ({
      number: index + 1,
      text: step.trim() + (step.trim().endsWith('.') ? '' : '.')
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center pixel-bg">
        <div className="pixel-box p-8 border-4" style={{ background: 'var(--bg)', borderColor: 'var(--green)' }}>
          <ChefHat className="w-16 h-16 animate-bounce mx-auto mb-4" style={{ color: 'var(--green)' }} />
          <p className="pixel-text" style={{ color: 'var(--green)' }}>LOADING YOUR KITCHEN...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      <div className="space-y-6">
      
      {/* Achievements */}
      {achievements.length > 0 && (
        <div className="pixel-box border-4 p-4" style={{ background: 'var(--bg)', borderColor: 'var(--purple)' }}>
          <p className="pixel-text text-xs mb-2" style={{ color: 'var(--purple)' }}>🏆 ACHIEVEMENTS ({achievements.length}/{achievementsList.length})</p>
          <div className="flex gap-2 flex-wrap">
            {achievementsList.filter(a => achievements.includes(a.id)).map(a => (
              <div key={a.id} className="pixel-badge border-2 px-3 py-1 text-xs" style={{ background: 'var(--surface)', borderColor: 'var(--purple)', color: 'var(--purple)' }}>
                {a.icon} {a.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pantry Management */}
      <div className="grid md:grid-cols-2 gap-4">
          
          {/* Ingredients */}
          <div className="pixel-box border-4 p-6" style={{ background: 'var(--bg)', borderColor: 'var(--green)' }}>
            <div className="flex items-center gap-2 mb-4">
              <Utensils className="w-6 h-6" style={{ color: 'var(--green)' }} />
              <h2 className="pixel-text text-xl" style={{ color: 'var(--green)' }}>INGREDIENTS ({ingredients.length})</h2>
            </div>
            
            <div className="relative mb-4">
              <input
                type="text"
                value={currentIngredient}
                onChange={(e) => {
                  setCurrentIngredient(e.target.value);
                  setShowIngredientSuggestions(true);
                }}
                onFocus={() => setShowIngredientSuggestions(true)}
                onKeyPress={(e) => e.key === 'Enter' && addIngredient(currentIngredient)}
                placeholder="TYPE TO ADD..."
                className="w-full px-4 py-3 border-2 pixel-text text-sm focus:outline-none"
                style={{ background: 'var(--surface)', borderColor: 'var(--green)', color: 'var(--green)' }}
              />
              <button
                onClick={() => addIngredient(currentIngredient)}
                className="absolute right-2 top-2 pixel-btn border-2 px-4 py-2 text-xs"
                style={{ background: 'var(--green)', borderColor: 'var(--green)', color: 'var(--bg)' }}
              >
                <Plus className="w-4 h-4" />
              </button>

              {/* Autocomplete Suggestions */}
              {showIngredientSuggestions && ingredientSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 border-2 mt-1 z-10 max-h-48 overflow-y-auto" style={{ background: 'var(--surface)', borderColor: 'var(--green)' }}>
                  {ingredientSuggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => addIngredient(suggestion)}
                      className="w-full text-left px-4 py-2 pixel-text text-xs border-b last:border-b-0"
                      style={{ color: 'var(--green)', borderColor: 'var(--green)' }}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
              {ingredients.length === 0 ? (
                <p className="pixel-text text-xs" style={{ color: 'var(--gray)' }}>Add ingredients to start cooking!</p>
              ) : (
                ingredients.map((ing, i) => (
                  <div key={i} className="pixel-badge border-2 px-3 py-1 flex items-center gap-2" style={{ background: 'var(--surface)', borderColor: 'var(--green)', color: 'var(--green)' }}>
                    <span className="pixel-text text-xs capitalize">{ing}</span>
                    <button onClick={() => removeIngredient(ing)} style={{ color: 'var(--red)' }}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Sauces */}
          <div className="pixel-box border-4 p-6" style={{ background: 'var(--bg)', borderColor: 'var(--pink)' }}>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-6 h-6" style={{ color: 'var(--pink)' }} />
              <h2 className="pixel-text text-xl" style={{ color: 'var(--pink)' }}>SAUCES & SEASONINGS ({sauces.length})</h2>
            </div>
            
            <div className="relative mb-4">
              <input
                type="text"
                value={currentSauce}
                onChange={(e) => {
                  setCurrentSauce(e.target.value);
                  setShowSauceSuggestions(true);
                }}
                onFocus={() => setShowSauceSuggestions(true)}
                onKeyPress={(e) => e.key === 'Enter' && addSauce(currentSauce)}
                placeholder="TYPE TO ADD..."
                className="w-full px-4 py-3 border-2 pixel-text text-sm focus:outline-none"
                style={{ background: 'var(--surface)', borderColor: 'var(--pink)', color: 'var(--pink)' }}
              />
              <button
                onClick={() => addSauce(currentSauce)}
                className="absolute right-2 top-2 pixel-btn border-2 px-4 py-2 text-xs"
                style={{ background: 'var(--pink)', borderColor: 'var(--pink)', color: 'var(--bg)' }}
              >
                <Plus className="w-4 h-4" />
              </button>

              {/* Autocomplete Suggestions */}
              {showSauceSuggestions && sauceSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 border-2 mt-1 z-10 max-h-48 overflow-y-auto" style={{ background: 'var(--surface)', borderColor: 'var(--pink)' }}>
                  {sauceSuggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => addSauce(suggestion)}
                      className="w-full text-left px-4 py-2 pixel-text text-xs border-b last:border-b-0"
                      style={{ color: 'var(--pink)', borderColor: 'var(--pink)' }}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
              {sauces.length === 0 ? (
                <p className="pixel-text text-xs" style={{ color: 'var(--gray)' }}>Add sauces to enhance your dishes!</p>
              ) : (
                sauces.map((sauce, i) => (
                  <div key={i} className="pixel-badge border-2 px-3 py-1 flex items-center gap-2" style={{ background: 'var(--surface)', borderColor: 'var(--pink)', color: 'var(--pink)' }}>
                    <span className="pixel-text text-xs capitalize">{sauce}</span>
                    <button onClick={() => removeSauce(sauce)} style={{ color: 'var(--red)' }}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Cuisine & Action Bar */}
        <div className="pixel-box border-4 p-6" style={{ background: 'var(--bg)', borderColor: 'var(--cyan)' }}>
          <div className="flex gap-4 flex-wrap items-center mb-4">
            <div className="flex items-center gap-2">
              <Globe className="w-6 h-6" style={{ color: 'var(--cyan)' }} />
              <span className="pixel-text text-sm" style={{ color: 'var(--cyan)' }}>CUISINE:</span>
            </div>
            <select
              value={selectedCuisine}
              onChange={(e) => setSelectedCuisine(e.target.value)}
              className="px-4 py-2 border-2 pixel-text text-sm focus:outline-none"
              style={{ background: 'var(--surface)', borderColor: 'var(--cyan)', color: 'var(--cyan)' }}
            >
              {cuisines.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            <button
              onClick={togglePuzzle}
              className="pixel-btn border-4 px-6 py-3 pixel-text"
              style={{ 
                background: showPuzzle ? 'var(--yellow)' : 'var(--gray)', 
                borderColor: showPuzzle ? 'var(--yellow)' : 'var(--gray)', 
                color: 'var(--bg)' 
              }}
            >
              <Shuffle className="w-5 h-5 inline mr-2" />
              PICK & MATCH MODE {showPuzzle ? 'ON' : 'OFF'}
            </button>
          </div>

          {/* Puzzle Mode */}
          {showPuzzle && (
            <div className="space-y-4 mb-4 p-4 border-2" style={{ background: 'var(--surface)', borderColor: 'var(--yellow)' }}>
              <p className="pixel-text text-xs" style={{ color: 'var(--yellow)' }}>SELECT SPECIFIC ITEMS TO MATCH:</p>
              <div>
                <p className="pixel-text text-xs mb-2" style={{ color: 'var(--green)' }}>INGREDIENTS:</p>
                <div className="flex flex-wrap gap-2">
                  {ingredients.map((ing, i) => (
                    <button
                      key={i}
                      onClick={() => handleSelectIngredient(ing)}
                      className="pixel-badge border-2 px-3 py-1 text-xs"
                      style={{ 
                        background: selectedIngredients.includes(ing) ? 'var(--green)' : 'var(--surface)', 
                        borderColor: 'var(--green)', 
                        color: selectedIngredients.includes(ing) ? 'var(--bg)' : 'var(--green)' 
                      }}
                    >
                      {selectedIngredients.includes(ing) && <Check className="w-3 h-3 inline mr-1" />}
                      {ing}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="pixel-text text-xs mb-2" style={{ color: 'var(--pink)' }}>SAUCES:</p>
                <div className="flex flex-wrap gap-2">
                  {sauces.map((sauce, i) => (
                    <button
                      key={i}
                      onClick={() => handleSelectSauce(sauce)}
                      className="pixel-badge border-2 px-3 py-1 text-xs"
                      style={{ 
                        background: selectedSauces.includes(sauce) ? 'var(--pink)' : 'var(--surface)', 
                        borderColor: 'var(--pink)', 
                        color: selectedSauces.includes(sauce) ? 'var(--bg)' : 'var(--pink)' 
                      }}
                    >
                      {selectedSauces.includes(sauce) && <Check className="w-3 h-3 inline mr-1" />}
                      {sauce}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-4 flex-wrap">
            <button
              onClick={findRecipes}
              disabled={ingredients.length === 0 && sauces.length === 0}
              className="flex-1 min-w-[200px] pixel-btn border-4 px-8 py-4 pixel-text text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'var(--cyan)', borderColor: 'var(--cyan)', color: 'var(--bg)' }}
            >
              <ChefHat className="w-6 h-6 inline mr-2" />
              FIND RECIPES!
            </button>
            <button
              onClick={surpriseMe}
              className="pixel-btn border-4 px-8 py-4 pixel-text text-lg"
              style={{ background: 'var(--purple)', borderColor: 'var(--purple)', color: 'var(--bg)' }}
            >
              <Shuffle className="w-6 h-6 inline mr-2" />
              SURPRISE ME!
            </button>
            <button
              onClick={() => {
                setIngredients([]);
                setSauces([]);
                setRecipes([]);
                savePantry([], []);
              }}
              className="pixel-btn border-4 px-6 py-4"
              style={{ background: 'var(--red)', borderColor: 'var(--red)', color: '#fff' }}
            >
              <Trash2 className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Results */}
        {recipes.length > 0 && (
          <div className="pixel-box border-4 p-6" style={{ background: 'var(--bg)', borderColor: 'var(--yellow)' }}>
            <div className="flex items-center gap-2 mb-6">
              <ChefHat className="w-8 h-8" style={{ color: 'var(--yellow)' }} />
              <h2 className="pixel-text text-2xl" style={{ color: 'var(--yellow)', letterSpacing: '0.1em' }}>QUEST RESULTS: {recipes.length} RECIPES FOUND</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {recipes.map((r, i) => (
                <div
                  key={i}
                  className="pixel-box border-4 p-6 transition-all duration-300"
                  style={{ 
                    background: 'var(--bg)', 
                    borderColor: highlightRecipe === r.name ? 'var(--yellow)' : r.matchPercentage >= 80 ? 'var(--green)' : r.matchPercentage >= 60 ? 'var(--yellow)' : 'var(--cyan)' 
                  }}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="pixel-text text-lg mb-1 flex items-center gap-2" style={{ color: 'var(--cyan)' }}>
                        <span className="text-2xl">{getCuisineFlag(r.cuisine)}</span>
                        {r.name.toUpperCase()} {r.special && <span style={{ color: 'var(--yellow)' }}>★</span>}
                      </h3>
                      <span className="pixel-text text-xs" style={{ color: 'var(--gray)' }}>{r.cuisine.toUpperCase()}</span>
                    </div>
                    <div className="pixel-badge px-3 py-1" style={{ 
                      background: r.matchPercentage >= 80 ? 'var(--green)' : r.matchPercentage >= 60 ? 'var(--yellow)' : 'var(--orange)', 
                      color: 'var(--bg)' 
                    }}>
                      {Math.round(r.matchPercentage)}%
                    </div>
                  </div>

                  <div className="flex gap-3 mb-3 flex-wrap">
                    <div className="pixel-tag border-2 px-2 py-1" style={{ background: 'var(--surface)', borderColor: 'var(--cyan)', color: 'var(--cyan)' }}>
                      <Clock className="w-3 h-3 inline mr-1" />
                      <span className="pixel-text text-xs">{r.time}</span>
                    </div>
                    <div className="pixel-tag border-2 px-2 py-1" style={{ 
                      background: 'var(--surface)', 
                      borderColor: r.difficulty === 'Easy' ? 'var(--green)' : r.difficulty === 'Medium' ? 'var(--yellow)' : 'var(--red)',
                      color: r.difficulty === 'Easy' ? 'var(--green)' : r.difficulty === 'Medium' ? 'var(--yellow)' : 'var(--red)'
                    }}>
                      <span className="pixel-text text-xs">{r.difficulty.toUpperCase()}</span>
                    </div>
                    <div className="pixel-tag border-2 px-2 py-1" style={{ background: 'var(--surface)', borderColor: 'var(--purple)', color: 'var(--purple)' }}>
                      <Star className="w-3 h-3 inline mr-1" />
                      <span className="pixel-text text-xs">+{r.points} PTS</span>
                    </div>
                  </div>

                  <div className="pixel-box border-2 p-2 mb-3" style={{ background: 'var(--surface)', borderColor: 'var(--gray)' }}>
                    <p className="pixel-text text-xs" style={{ color: 'var(--green)' }}>MATCH: {r.ingredientMatches} INGREDIENTS, {r.sauceMatches} SAUCES</p>
                  </div>

                  <p className="pixel-text text-sm mb-4 leading-relaxed" style={{ color: 'var(--fg)' }}>
                    {getRecipeDescription(r)}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {r.ingredients.map((ing, k) => <span key={k} className="pixel-tag border px-2 py-1 text-xs" style={{ background: 'var(--surface)', borderColor: 'var(--green)', color: 'var(--green)' }}>{ing}</span>)}
                    {r.sauces.map((s, k) => <span key={k} className="pixel-tag border px-2 py-1 text-xs" style={{ background: 'var(--surface)', borderColor: 'var(--pink)', color: 'var(--pink)' }}>{s}</span>)}
                  </div>

                  <div className="flex gap-2">
                    <button 
                      onClick={() => setSelectedRecipeForModal(r)} 
                      className="flex-1 pixel-btn border-4 py-3 pixel-text"
                      style={{ background: 'var(--cyan)', borderColor: 'var(--cyan)', color: 'var(--bg)' }}
                    >
                      📖 VIEW RECIPE
                    </button>
                    <button 
                      onClick={() => cookRecipe(r)} 
                      className="flex-1 pixel-btn border-4 py-3 pixel-text"
                      style={{ background: 'var(--yellow)', borderColor: 'var(--yellow)', color: 'var(--bg)' }}
                    >
                      <Zap className="w-5 h-5 inline mr-2" /> COOK! (+{r.points})
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {recipes.length === 0 && (ingredients.length > 0 || sauces.length > 0) && !showPuzzle && (
          <div className="pixel-box p-8 border-4 text-center" style={{ background: 'var(--bg)', borderColor: 'var(--gray)' }}>
            <ChefHat className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--gray)' }} />
            <p className="pixel-text text-lg" style={{ color: 'var(--gray)' }}>PRESS "FIND RECIPES" OR "SURPRISE ME" TO START YOUR QUEST! 🎮</p>
          </div>
        )}

      {/* Recipe Detail Modal */}
      {selectedRecipeForModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedRecipeForModal(null)}>
          <div className="pixel-box border-4 p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" style={{ background: 'var(--bg)', borderColor: 'var(--yellow)' }} onClick={(e) => e.stopPropagation()}>
            
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="pixel-text text-3xl mb-2 flex items-center gap-2" style={{ color: 'var(--yellow)' }}>
                  <span className="text-4xl">{getCuisineFlag(selectedRecipeForModal.cuisine)}</span>
                  {selectedRecipeForModal.name.toUpperCase()}
                  {selectedRecipeForModal.special && <span style={{ color: 'var(--yellow)' }}>★</span>}
                </h2>
                <p className="pixel-text text-sm" style={{ color: 'var(--cyan)' }}>{selectedRecipeForModal.cuisine} Cuisine</p>
              </div>
              <button onClick={() => setSelectedRecipeForModal(null)} className="pixel-btn border-4 px-4 py-2" style={{ background: 'var(--red)', borderColor: 'var(--red)', color: '#fff' }}>
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="pixel-box border-2 p-3 text-center" style={{ background: 'var(--surface)', borderColor: 'var(--cyan)' }}>
                <Clock className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--cyan)' }} />
                <p className="pixel-text text-xs" style={{ color: 'var(--cyan)' }}>{selectedRecipeForModal.time}</p>
              </div>
              <div className="pixel-box border-2 p-3 text-center" style={{ 
                background: 'var(--surface)', 
                borderColor: selectedRecipeForModal.difficulty === 'Easy' ? 'var(--green)' : selectedRecipeForModal.difficulty === 'Medium' ? 'var(--yellow)' : 'var(--red)'
              }}>
                <Star className="w-6 h-6 mx-auto mb-2" style={{ 
                  color: selectedRecipeForModal.difficulty === 'Easy' ? 'var(--green)' : selectedRecipeForModal.difficulty === 'Medium' ? 'var(--yellow)' : 'var(--red)'
                }} />
                <p className="pixel-text text-xs" style={{ 
                  color: selectedRecipeForModal.difficulty === 'Easy' ? 'var(--green)' : selectedRecipeForModal.difficulty === 'Medium' ? 'var(--yellow)' : 'var(--red)'
                }}>{selectedRecipeForModal.difficulty}</p>
              </div>
              <div className="pixel-box border-2 p-3 text-center" style={{ background: 'var(--surface)', borderColor: 'var(--purple)' }}>
                <Trophy className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--purple)' }} />
                <p className="pixel-text text-xs" style={{ color: 'var(--purple)' }}>+{selectedRecipeForModal.points} PTS</p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="pixel-text text-xl mb-3 flex items-center gap-2" style={{ color: 'var(--green)' }}>
                <Utensils className="w-5 h-5" /> INGREDIENTS
              </h3>
              <div className="pixel-box border-2 p-4" style={{ background: 'var(--surface)', borderColor: 'var(--green)' }}>
                <ul className="space-y-2">
                  {selectedRecipeForModal.ingredients.map((ing, i) => (
                    <li key={i} className="pixel-text text-sm flex items-start gap-2" style={{ color: 'var(--green)' }}>
                      <span style={{ color: 'var(--yellow)' }}>•</span>
                      <span className="capitalize">{ing}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="pixel-text text-xl mb-3 flex items-center gap-2" style={{ color: 'var(--pink)' }}>
                <Sparkles className="w-5 h-5" /> SAUCES & SEASONINGS
              </h3>
              <div className="pixel-box border-2 p-4" style={{ background: 'var(--surface)', borderColor: 'var(--pink)' }}>
                <ul className="space-y-2">
                  {selectedRecipeForModal.sauces.map((sauce, i) => (
                    <li key={i} className="pixel-text text-sm flex items-start gap-2" style={{ color: 'var(--pink)' }}>
                      <span style={{ color: 'var(--yellow)' }}>•</span>
                      <span className="capitalize">{sauce}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="pixel-text text-xl mb-3 flex items-center gap-2" style={{ color: 'var(--cyan)' }}>
                <ChefHat className="w-5 h-5" /> COOKING INSTRUCTIONS
              </h3>
              <div className="pixel-box border-2 p-4" style={{ background: 'var(--surface)', borderColor: 'var(--cyan)' }}>
                <ol className="space-y-3">
                  {formatInstructions(selectedRecipeForModal.instructions).map((step, i) => (
                    <li key={i} className="pixel-text text-sm leading-relaxed flex gap-3" style={{ color: 'var(--fg)' }}>
                      <span className="font-bold flex-shrink-0" style={{ color: 'var(--yellow)' }}>{step.number}.</span>
                      <span>{step.text}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            <div className="flex gap-4 justify-center">
              <button 
                onClick={() => {
                  cookRecipe(selectedRecipeForModal);
                  setSelectedRecipeForModal(null);
                }}
                className="pixel-btn border-4 px-8 py-4 pixel-text text-xl"
                style={{ background: 'var(--yellow)', borderColor: 'var(--yellow)', color: 'var(--bg)' }}
              >
                <Zap className="w-6 h-6 inline mr-2" /> COOK THIS! (+{selectedRecipeForModal.points} PTS)
              </button>
              <button 
                onClick={() => setSelectedRecipeForModal(null)}
                className="pixel-btn border-4 px-8 py-4 pixel-text text-xl"
                style={{ background: 'var(--gray)', borderColor: 'var(--gray)', color: 'var(--fg)' }}
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default Game;
